'use client';

import { useMemo, useState } from 'react';
import { convertStrokeToFill } from '@/lib/browser-cssanim-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const SAMPLE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="#6366f1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <path d="M20 6 9 17l-5-5" />
</svg>`;

export default function BrowserSvgStrokeToFill() {
  const [input, setInput] = useState(SAMPLE_SVG);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const result = useMemo(() => convertStrokeToFill(input), [input]);

  async function handleCopy() {
    try {
      await copyText(result.output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">SVG</span>
        <h2 className="tool-live-title">SVG stroke to fill converter</h2>
        <p className="tool-live-subtitle">
          Paste SVG markup drawn with strokes and convert each stroked shape to a filled equivalent,
          then copy the result.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>SVG markup</span>
              <textarea
                onChange={(e) => setInput(e.target.value)}
                rows={12}
                spellCheck={false}
                value={input}
              />
            </label>
          </div>

          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleCopy} type="button">
              Copy result
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <div
            className="tool-live-preview-frame"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-around',
              gap: 16,
              minHeight: 160,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div
                dangerouslySetInnerHTML={{ __html: input }}
                style={{ maxWidth: 100, margin: '0 auto' }}
              />
              <p className="tool-live-panel-note" style={{ marginTop: 8 }}>
                Original
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div
                dangerouslySetInnerHTML={{ __html: result.output }}
                style={{ maxWidth: 100, margin: '0 auto' }}
              />
              <p className="tool-live-panel-note" style={{ marginTop: 8 }}>
                Filled
              </p>
            </div>
          </div>

          <label className="tool-live-field tool-live-field-full">
            <span>Converted SVG</span>
            <textarea readOnly rows={10} value={result.output} />
          </label>
          {result.notes.map((note, index) => (
            <p
              className="status-message"
              key={index}
              style={{ color: result.changed === 0 && index === 0 ? '#dc2626' : undefined }}
            >
              {note}
            </p>
          ))}
          <p className="tool-live-panel-note">Generated locally in your browser.</p>
        </section>
      </div>
    </div>
  );
}
